import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  Save, Eye, Image as ImageIcon, Sparkles, Tag, Layers, Mail, 
  ChevronRight, ArrowLeft, Palette, Type, Layout, List
} from 'lucide-react';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

// Bullet Style Options
const bulletStyles = [
  { id: 'square', name: 'Quadrat', symbol: '▪' },
  { id: 'square-outline', name: 'Quadrat (Rahmen)', symbol: '▫' },
  { id: 'diamond', name: 'Raute', symbol: '◆' },
  { id: 'diamond-outline', name: 'Raute (Rahmen)', symbol: '◇' },
  { id: 'circle', name: 'Kreis', symbol: '●' },
  { id: 'circle-outline', name: 'Kreis (Rahmen)', symbol: '○' },
  { id: 'check', name: 'Häkchen', symbol: '✓' },
  { id: 'arrow', name: 'Pfeil', symbol: '→' },
  { id: 'star', name: 'Stern', symbol: '★' },
  { id: 'heart', name: 'Herz', symbol: '♥' },
];

const bulletColors = [
  { name: 'Goldbraun', color: '#C4A084' },
  { name: 'Braun', color: '#8B7355' },
  { name: 'Dunkelbraun', color: '#5D4E3C' },
  { name: 'Schwarz', color: '#333333' },
  { name: 'Grau', color: '#6B7280' },
  { name: 'Grün', color: '#10B981' },
  { name: 'Blau', color: '#3B82F6' },
  { name: 'Rosa', color: '#EC4899' },
  { name: 'Rot', color: '#DC2626' },
  { name: 'Orange', color: '#F97316' },
];

// Alle bearbeitbaren Bereiche
const DESIGN_SECTIONS = [
  {
    id: 'hero',
    name: 'Hero Banner',
    description: 'Das große Willkommens-Banner oben auf der Startseite',
    icon: ImageIcon,
    color: '#E8D5F0'
  },
  {
    id: 'seasonal',
    name: 'Saisonale Highlights',
    description: 'Bereich für saisonale Angebote (Ostern, Weihnachten, etc.)',
    icon: Sparkles,
    color: '#D5E8F0'
  },
  {
    id: 'coupon',
    name: 'Rabatt-Banner',
    description: 'Zeigt aktiven Gutscheincode an',
    icon: Tag,
    color: '#D5F0E0'
  },
  {
    id: 'catalogs',
    name: 'Produktkataloge',
    description: 'Kategorien-Übersicht auf der Startseite',
    icon: Layers,
    color: '#F0E8D5'
  },
  {
    id: 'newsletter',
    name: 'Newsletter-Anmeldebereich',
    description: 'Newsletter-Anmeldung auf der Startseite',
    icon: Mail,
    color: '#F0D5E0'
  },
  {
    id: 'newsletter-designer',
    name: 'Newsletter erstellen & versenden',
    description: 'Newsletter visuell gestalten, KI-Entwurf, Vorlagen, Freigabe-Workflow',
    icon: Sparkles,
    color: '#F5E0F0',
    external_link: '/admin/newsletter-designer'
  },
  {
    id: 'bullets',
    name: 'Aufzählungszeichen',
    description: 'Stil der Listen in Produktbeschreibungen',
    icon: List,
    color: '#E0F0D5'
  }
];

// Standard-Einstellungen
const DEFAULT_SETTINGS = {
  hero: {
    title: 'Willkommen bei Homeyoursoul',
    subtitle: 'Entdecken Sie unsere exklusive Kollektion',
    background_color: '#E8E0D5',
    text_color: '#6D5A47',
    button_color: '#C4A084',
    button_text: 'Jetzt entdecken',
    is_active: true
  },
  seasonal: {
    title: 'Saisonale Highlights',
    subtitle: 'Aktuelle Angebote für Sie',
    background_color: '#FFF8E8',
    accent_color: '#C4A084',
    is_active: true
  },
  coupon: {
    title: 'Exklusiver Rabatt',
    code: 'WILLKOMMEN10',
    discount_percent: 10,
    background_color: '#E8F5E9',
    text_color: '#2E7D32',
    is_active: false
  },
  catalogs: {
    title: 'Unsere Kategorien',
    columns: 2,
    show_images: true,
    background_color: '#FFFFFF',
    is_active: true
  },
  newsletter: {
    title: 'Newsletter',
    subtitle: 'Erhalten Sie exklusive Angebote',
    button_text: 'Anmelden',
    background_color: '#F5EDE4',
    button_color: '#C4A084',
    is_active: true
  }
};

const VisualDesignEditorNew = () => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Bullet Style State
  const [bulletStyle, setBulletStyle] = useState({ style: 'square', color: '#C4A084' });
  const [bulletLoading, setBulletLoading] = useState(false);

  useEffect(() => {
    loadSettings();
    loadBulletStyle();
  }, []);

  const loadBulletStyle = async () => {
    try {
      const response = await axiosInstance.get('/settings/bullet-style');
      setBulletStyle(response.data);
    } catch (error) {
      console.error('Error loading bullet style:', error);
    }
  };

  const saveBulletStyle = async (style, color) => {
    setBulletLoading(true);
    try {
      await axiosInstance.put('/settings/bullet-style', { style, color });
      setBulletStyle({ style, color });
      toast.success('Aufzählungszeichen gespeichert!');
    } catch (error) {
      console.error('Error saving bullet style:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setBulletLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/design/settings');
      setSettings(response.data || DEFAULT_SETTINGS);
    } catch (error) {
      console.log('Using default settings');
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/design/settings', settings);
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      // Save to local endpoint if main fails
      try {
        await axiosInstance.post('/homepage-sections/admin/design-settings', settings);
        toast.success('Einstellungen gespeichert!');
      } catch (e) {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (sectionId, key, value) => {
    setSettings(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [key]: value
      }
    }));
  };

  const getSectionSettings = (sectionId) => {
    return settings[sectionId] || DEFAULT_SETTINGS[sectionId] || {};
  };

  // Hauptansicht - Liste aller Bereiche
  if (!selectedSection) {
    return (
      <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="max-w-2xl mx-auto">
          <AdminPageHeader 
            title="Design bearbeiten" 
            subtitle="Wählen Sie einen Bereich zum Bearbeiten"
            icon={Palette}
          />

          {/* Einfache Anleitung */}
          <div className="mb-4 p-3 rounded-none" style={{ backgroundColor: '#E8F4E8', border: '1px solid #90C090' }}>
            <p className="text-sm text-green-800">
              <strong>So geht's:</strong> Klicken Sie auf einen Bereich, um Texte, Farben und Einstellungen zu bearbeiten.
            </p>
          </div>

          {/* Bereichs-Liste */}
          <div className="space-y-2">
            {DESIGN_SECTIONS.map((section) => {
              const SectionIcon = section.icon;
              const sectionSettings = getSectionSettings(section.id);
              
              return (
                <Card
                  key={section.id}
                  className="rounded-none border cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: '#C4A084' }}
                  onClick={() => {
                    if (section.external_link) {
                      window.location.href = section.external_link;
                    } else {
                      setSelectedSection(section);
                    }
                  }}
                  data-testid={`design-section-${section.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div 
                        className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: section.color }}
                      >
                        <SectionIcon className="h-6 w-6 text-gray-700" />
                      </div>

                      {/* Name und Beschreibung */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">
                          {section.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {section.description}
                        </p>
                      </div>

                      {/* Status */}
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          sectionSettings.is_active !== false 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {sectionSettings.is_active !== false ? 'Aktiv' : 'Inaktiv'}
                        </span>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Detail-Ansicht für einen Bereich (wie Hero Banner Editor)
  const currentSettings = getSectionSettings(selectedSection.id);
  const SectionIcon = selectedSection.icon;

  // Spezielle Ansicht für Aufzählungszeichen
  if (selectedSection.id === 'bullets') {
    return (
      <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="max-w-2xl mx-auto">
          {/* Zurück-Button */}
          <Button
            variant="outline"
            onClick={() => setSelectedSection(null)}
            className="mb-4 rounded-none"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zur Übersicht
          </Button>

          {/* Header */}
          <div className="mb-4 p-3 flex items-center gap-3" style={{ backgroundColor: '#AF9774' }}>
            <div 
              className="w-10 h-10 rounded flex items-center justify-center"
              style={{ backgroundColor: selectedSection.color }}
            >
              <SectionIcon className="h-5 w-5 text-gray-700" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{selectedSection.name}</h1>
              <p className="text-sm text-white/80">{selectedSection.description}</p>
            </div>
          </div>

          {/* Vorschau */}
          <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Vorschau:</p>
              <div className="space-y-2 bg-gray-50 p-4 rounded-none">
                <div className="flex items-start gap-2 text-gray-600">
                  <span style={{ color: bulletStyle.color }} className="mt-0.5">
                    {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                  </span>
                  <span>verschiedene Farben</span>
                </div>
                <div className="flex items-start gap-2 text-gray-600">
                  <span style={{ color: bulletStyle.color }} className="mt-0.5">
                    {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                  </span>
                  <span>hochwertige Materialien</span>
                </div>
                <div className="flex items-start gap-2 text-gray-600">
                  <span style={{ color: bulletStyle.color }} className="mt-0.5">
                    {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                  </span>
                  <span>handgemacht mit Liebe</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form wählen */}
          <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <List className="h-4 w-4 text-white" />
            <h2 className="text-sm font-semibold text-white">Form wählen</h2>
          </div>
          
          <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
            <CardContent className="p-4">
              <div className="grid grid-cols-5 gap-2">
                {bulletStyles.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => saveBulletStyle(style.id, bulletStyle.color)}
                    disabled={bulletLoading}
                    className={`p-3 border-2 rounded-none flex flex-col items-center justify-center transition-all ${
                      bulletStyle.style === style.id 
                        ? 'border-[#C4A084] bg-[#F5EDE4]' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span className="text-2xl" style={{ color: bulletStyle.color }}>{style.symbol}</span>
                    <span className="text-xs mt-1 text-gray-600">{style.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Farbe wählen */}
          <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
            <Palette className="h-4 w-4 text-white" />
            <h2 className="text-sm font-semibold text-white">Farbe wählen</h2>
          </div>
          
          <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {bulletColors.map((color) => (
                  <button
                    key={color.color}
                    type="button"
                    onClick={() => saveBulletStyle(bulletStyle.style, color.color)}
                    disabled={bulletLoading}
                    className={`w-10 h-10 rounded-none border-2 transition-all ${
                      bulletStyle.color === color.color 
                        ? 'border-[#5D4E3C] ring-2 ring-[#C4A084]' 
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                    style={{ backgroundColor: color.color }}
                    title={color.name}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-gray-500 mt-4">
            Tipp: Beginnen Sie Zeilen in der Produktbeschreibung mit "-" oder "•" um automatisch Aufzählungszeichen zu erstellen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-2xl mx-auto">
        {/* Zurück-Button */}
        <Button
          variant="outline"
          onClick={() => setSelectedSection(null)}
          className="mb-4 rounded-none"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Übersicht
        </Button>

        {/* Header */}
        <div className="mb-4 p-3 flex items-center gap-3" style={{ backgroundColor: '#AF9774' }}>
          <div 
            className="w-10 h-10 rounded flex items-center justify-center"
            style={{ backgroundColor: selectedSection.color }}
          >
            <SectionIcon className="h-5 w-5 text-gray-700" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">{selectedSection.name}</h1>
            <p className="text-sm text-white/80">{selectedSection.description}</p>
          </div>
        </div>

        {/* Aktiv/Inaktiv Schalter */}
        <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">Bereich anzeigen</Label>
                <p className="text-sm text-gray-500">Auf der Startseite sichtbar</p>
              </div>
              <Switch
                checked={currentSettings.is_active !== false}
                onCheckedChange={(checked) => updateSetting(selectedSection.id, 'is_active', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Texte bearbeiten */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <Type className="h-4 w-4 text-white" />
          <h2 className="text-sm font-semibold text-white">Texte</h2>
        </div>
        
        <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
          <CardContent className="p-4 space-y-4">
            {/* Titel */}
            <div>
              <Label>Überschrift</Label>
              <Input
                value={currentSettings.title || ''}
                onChange={(e) => updateSetting(selectedSection.id, 'title', e.target.value)}
                placeholder="Überschrift eingeben..."
                className="rounded-none mt-1"
              />
            </div>

            {/* Untertitel (wenn vorhanden) */}
            {currentSettings.subtitle !== undefined && (
              <div>
                <Label>Untertitel</Label>
                <Textarea
                  value={currentSettings.subtitle || ''}
                  onChange={(e) => updateSetting(selectedSection.id, 'subtitle', e.target.value)}
                  placeholder="Untertitel eingeben..."
                  className="rounded-none mt-1"
                  rows={2}
                />
              </div>
            )}

            {/* Button-Text (wenn vorhanden) */}
            {currentSettings.button_text !== undefined && (
              <div>
                <Label>Button-Text</Label>
                <Input
                  value={currentSettings.button_text || ''}
                  onChange={(e) => updateSetting(selectedSection.id, 'button_text', e.target.value)}
                  placeholder="z.B. Jetzt entdecken"
                  className="rounded-none mt-1"
                />
              </div>
            )}

            {/* Rabatt-Code (nur für Coupon) */}
            {selectedSection.id === 'coupon' && (
              <>
                <div>
                  <Label>Rabatt-Code</Label>
                  <Input
                    value={currentSettings.code || ''}
                    onChange={(e) => updateSetting(selectedSection.id, 'code', e.target.value.toUpperCase())}
                    placeholder="z.B. WILLKOMMEN10"
                    className="rounded-none mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label>Rabatt in %</Label>
                  <Input
                    type="number"
                    value={currentSettings.discount_percent || 0}
                    onChange={(e) => updateSetting(selectedSection.id, 'discount_percent', parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="rounded-none mt-1"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Farben bearbeiten */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <Palette className="h-4 w-4 text-white" />
          <h2 className="text-sm font-semibold text-white">Farben</h2>
        </div>

        <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
          <CardContent className="p-4 space-y-4">
            {/* Hintergrundfarbe */}
            <div>
              <Label>Hintergrundfarbe</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={currentSettings.background_color || '#FFFFFF'}
                  onChange={(e) => updateSetting(selectedSection.id, 'background_color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={currentSettings.background_color || '#FFFFFF'}
                  onChange={(e) => updateSetting(selectedSection.id, 'background_color', e.target.value)}
                  className="rounded-none flex-1 font-mono"
                  placeholder="#FFFFFF"
                />
              </div>
            </div>

            {/* Textfarbe (wenn vorhanden) */}
            {currentSettings.text_color !== undefined && (
              <div>
                <Label>Textfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    value={currentSettings.text_color || '#000000'}
                    onChange={(e) => updateSetting(selectedSection.id, 'text_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={currentSettings.text_color || '#000000'}
                    onChange={(e) => updateSetting(selectedSection.id, 'text_color', e.target.value)}
                    className="rounded-none flex-1 font-mono"
                    placeholder="#000000"
                  />
                </div>
              </div>
            )}

            {/* Button-Farbe (wenn vorhanden) */}
            {currentSettings.button_color !== undefined && (
              <div>
                <Label>Button-Farbe</Label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    value={currentSettings.button_color || '#C4A084'}
                    onChange={(e) => updateSetting(selectedSection.id, 'button_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={currentSettings.button_color || '#C4A084'}
                    onChange={(e) => updateSetting(selectedSection.id, 'button_color', e.target.value)}
                    className="rounded-none flex-1 font-mono"
                    placeholder="#C4A084"
                  />
                </div>
              </div>
            )}

            {/* Schnellauswahl beliebter Farben */}
            <div>
              <Label className="text-xs text-gray-500">Schnellauswahl</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['#E8E0D5', '#C4A084', '#AF9774', '#6D5A47', '#FFFFFF', '#F5EDE4', '#FFF8E8', '#E8F5E9'].map(color => (
                  <button
                    key={color}
                    onClick={() => updateSetting(selectedSection.id, 'background_color', color)}
                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-colors"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Layout-Optionen (nur für bestimmte Bereiche) */}
        {selectedSection.id === 'catalogs' && (
          <>
            <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
              <Layout className="h-4 w-4 text-white" />
              <h2 className="text-sm font-semibold text-white">Layout</h2>
            </div>

            <Card className="rounded-none border mb-4" style={{ borderColor: '#C4A084' }}>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label>Spalten pro Reihe</Label>
                  <div className="flex gap-2 mt-2">
                    {[2, 3, 4].map(cols => (
                      <button
                        key={cols}
                        onClick={() => updateSetting(selectedSection.id, 'columns', cols)}
                        className={`px-4 py-2 border rounded-none ${
                          currentSettings.columns === cols 
                            ? 'bg-[#AF9774] text-white border-[#AF9774]' 
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {cols} Spalten
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Bilder anzeigen</Label>
                    <p className="text-sm text-gray-500">Kategorie-Bilder einblenden</p>
                  </div>
                  <Switch
                    checked={currentSettings.show_images !== false}
                    onCheckedChange={(checked) => updateSetting(selectedSection.id, 'show_images', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Live-Vorschau */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <Eye className="h-4 w-4 text-white" />
          <h2 className="text-sm font-semibold text-white">Live-Vorschau</h2>
        </div>

        <Card className="rounded-none border mb-4 overflow-hidden" style={{ borderColor: '#C4A084' }}>
          <div 
            className="p-6 min-h-[150px]"
            style={{ 
              backgroundColor: currentSettings.background_color || '#FFFFFF',
              color: currentSettings.text_color || '#000000'
            }}
          >
            <h3 className="text-xl font-bold mb-2">{currentSettings.title || 'Überschrift'}</h3>
            {currentSettings.subtitle && (
              <p className="text-sm opacity-80 mb-4">{currentSettings.subtitle}</p>
            )}
            {currentSettings.button_text && (
              <button 
                className="px-4 py-2 text-white text-sm"
                style={{ backgroundColor: currentSettings.button_color || '#C4A084' }}
              >
                {currentSettings.button_text}
              </button>
            )}
            {selectedSection.id === 'coupon' && currentSettings.code && (
              <div className="mt-4 p-3 bg-white/20 rounded inline-block">
                <span className="font-mono font-bold text-lg">{currentSettings.code}</span>
                <span className="ml-2">= {currentSettings.discount_percent}% Rabatt</span>
              </div>
            )}
          </div>
        </Card>

        {/* Speichern-Button */}
        <Button 
          onClick={saveSettings}
          disabled={saving}
          className="w-full rounded-none"
          style={{ backgroundColor: '#AF9774' }}
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
        </Button>
      </div>
    </div>
  );
};

export default VisualDesignEditorNew;
